import React, { useEffect } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VSNImage from "../../shared/VSNImage";
import { ReactComponent as Round } from "../../assets/admin/svg/org-image.svg";
import VSNTypography from "../../shared/VSNTypography";
import VSNButton from "../../shared/VSNButton";
import { clearFormData, toggleModelShow } from "../../shared/CustomFunc";
import shape1 from "../../assets/admin/png/model-shape-1.png";
import shape2 from "../../assets/admin/png/model-shape-2.png";
import {
  ON_FLAG_CHANGE,
  ON_FORM_CHANGE,
  SET_API_DATA,
} from "../../redux/constants";
import { ApiContainer } from "../../utils/api";
import VSNLoader from "../../shared/VSNLoader";

const formPath = { parent: "eventScheduleInfoStatus" };
const useStyles = makeStyles((theme) => ({
  heading: {
    position: "sticky",
    top: 0,
    borderBottom: "1px solid var(--primary)",
    padding: "12px 25px",
    margin: "0 -25px",
    backgroundColor: "var(--white)",
    zIndex: 2,
    [theme.breakpoints.down("sm")]: {
      padding: "12px 15px",
      margin: "0 -15px",
    },
    "&:after": {
      position: "absolute",
      content: "''",
      left: 0,
      right: 0,
      height: 25,
      width: "100%",
      bottom: "100%",
      background: "var(--white)",
      zIndex: 8,
    },
    "& svg": {
      borderRadius: "50%",
      [theme.breakpoints.down("md")]: {
        width: 40,
        height: 40,
      },
      [theme.breakpoints.down("sm")]: {
        width: 30,
        height: 30,
      },
    },
    "& h5": {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: "31px",
      marginLeft: 16,
      [theme.breakpoints.down("md")]: {
        fontSize: 18,
        lineHeight: "26px",
        maxWidth: "70%",
      },
    },
  },
  detailHeading: {
    "&.MuiTypography-root": {
      color: "rgba(134, 101, 10, 1)",
      fontSize: 14,
      fontWeight: 700,
      marginBottom: 24,
      [theme.breakpoints.down("md")]: {
        marginBottom: 20,
      },
    },
  },
  cancelButton: {
    "&.MuiButtonBase-root": {
      padding: [[12, 62]],
      [theme.breakpoints.down("md")]: {
        padding: [[8, 15]],
      },
    },
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: "#F6F6FD",
    padding: "20px 25px",
    gap: 10,
    marginLeft: "-25px",
    marginRight: "-25px",
    position: "sticky",
    bottom: 0,
    zIndex: 99,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      padding: "15px",
      gap: 10,
      marginLeft: "-15px",
      marginRight: "-15px",
    },
    "& .MuiButtonBase-root": {
      margin: 0,
      padding: "9.5px 25px",
      lineHeight: "21px",
      width: "auto",
      minWidth: "170px",
      borderRadius: 10,
      "& span": {
        fontSize: 16,
        fontWeight: 700,
      },
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
  },
  shape: {
    position: "absolute",
    zIndex: -2,
  },
  shape1: {
    top: "20%",
    right: "10%",
  },
  shape2: {
    bottom: "5%",
    left: "0%",
  },
  formGrid: {
    "& .react-tel-input": {
      "& .form-control": {
        fontSize: 18,
        fontWeight: 700,
        color: "#202024",
      },
    },
    "& .MuiFormControl-root.MuiTextField-root": {
      width: "100%",
    },
  },
  inviteButton: {
    background: "#2D8F4E0D !important",
    "&.MuiButtonBase-root": {
      color: "#2D8F4E !important",
      padding: "8.5px 10px",
      borderRadius: 5,
      border: 0,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },

      "& .content": {
        fontWeight: 500,
        fontSize: 14,
        lineHeight: "16px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 10,
      },

      "&.width": {
        minWidth: "133px",
      },
    },
  },
}));

const EventStatusSchedule = ({
  organizationId,
  locationId,
  departmentId,
  eventId,
  handleViewEventSchedule,
}) => {
  const organization_original_id = useSelector(
    (state) => state?.app?.userData?.user_profile?.organization_original_id
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const eventScheduleInfo = useSelector(
    (state) => state.form.formData?.[formPath.parent]
  );
  const eventScheduleData = useSelector(
    (state) => state.api?.eventScheduleData
  );
  const form = useSelector((state) => state?.form);
  const role = useSelector((state) => state?.app?.userData?.user_profile?.role);

  const refetchEvents = useSelector((state) => state.app.flags?.refetchEvents);
  const { api } = ApiContainer();
  const navigate = useNavigate();

  const handelView = (id) => {
    navigate(
      `/org-admin/${organization_original_id ?? organizationId}/locations/${
        eventScheduleData?.location_original_id ?? locationId
      }/departments/${
        eventScheduleData?.department_original_id ?? departmentId
      }/event/${id ?? eventId}`
    );
  };

  const handelCreateEventView = () => {
    navigate(
      `/org-admin/${organization_original_id ?? organizationId}/locations/${
        eventScheduleData?.location_original_id ?? locationId
      }/departments/${
        eventScheduleData?.department_original_id ?? departmentId
      }/event`
    );
  };

  useEffect(() => {
    if (refetchEvents) {
      dispatch({
        type: ON_FLAG_CHANGE,
        payload: { refetchEvents: false },
      });
      const handelEventScheduleClick = async () => {
        const data = await api({
          method: "GET",
          endPoint: `organization/${organization_original_id}/locations/${eventScheduleData?.location_original_id}/departments/${eventScheduleData?.department_original_id}/event-schedule`,
          showToastMessage: false,
          urlencoded: false,
          needLoader: true,
          parent: "changingRole",
          needFullResponse: false,
        });
        if (data?.status) {
          dispatch({
            type: ON_FORM_CHANGE,
            payload: {
              formData: {
                ...form.formData,
                eventScheduleInfoStatus: data?.data,
              },
            },
          });
        }
      };
      handelEventScheduleClick();
    }
  }, [refetchEvents]);

  return (
    <Box>
      <>
        <VSNImage
          src={shape1}
          alt=""
          className={`${classes.shape} ${classes.shape1}`}
        />
        <VSNImage
          src={shape2}
          alt=""
          className={`${classes.shape} ${classes.shape2}`}
        />
        <Stack direction="row" alignItems="center" className={classes.heading}>
          <Stack>
            <Round />
          </Stack>
          <VSNTypography variant="h5" component="h5">
            Event Schedule
          </VSNTypography>
        </Stack>
      </>

      <Box pt={2} pb={1}>
        {role !== "department_admin" && (
          <VSNButton
            sx={{ ml: "auto", px: 2, py: 1 }}
            // handleClick={() => {
            //   dispatch(toggleModelShow("createEventSchedule", true));
            //   dispatch(clearFormData("eventScheduleInfo"));
            // }}
            handleClick={() => handelCreateEventView()}
            startIcon={<AddIcon />}
          >
            Create Event
          </VSNButton>
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Event ID</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Event Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(eventScheduleInfo) &&
            eventScheduleInfo?.length > 0 ? (
              eventScheduleInfo?.map((el, index) => (
                <TableRow key={index}>
                  <TableCell>{el?.event_id ?? "-"}</TableCell>
                  <TableCell>
                    {el?.start_date &&
                      dayjs(el?.start_date)?.format("MM/DD/YYYY")}
                  </TableCell>
                  <TableCell>
                    {el?.end_date && dayjs(el?.end_date)?.format("MM/DD/YYYY")}
                  </TableCell>

                  <TableCell>
                    <VSNButton
                      className={`${classes.inviteButton}  width`}
                      handleClick={() => {
                        if (role === "department_admin") {
                          handleViewEventSchedule(el?.id);
                        } else {
                          handelView(el?.id);
                        }
                      }}
                    >
                      View Event
                    </VSNButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableCell colSpan={4} align="center">
                No Event Found
              </TableCell>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        className={classes.buttons}
      >
        <VSNButton
          handleClick={() =>
            role === "department_admin"
              ? dispatch(toggleModelShow("viewEventScheduleDepartmentAdmin"))
              : dispatch(toggleModelShow("EventStatusScheduleFromDepartment"))
          }
          variant="outlined"
          className={classes.cancelButton}
        >
          Cancel
        </VSNButton>
      </Stack>
    </Box>
  );
};

export default EventStatusSchedule;
